import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Synchronous guard against double-submitting a POD copy. Set before the first
   * await in onCopyPod() and cleared in its finally block, so a fast double-click
   * can never fire two clone+add sequences for the same source pod.
   * @type {boolean}
   */
  #copyInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Render inactive items on initial load
    this.#renderInactiveItems();

    // Clean up orphaned sub-products on page load
    this.#cleanupOrphanedSubProducts();

    // Clean up localStorage for pods that are already in the cart
    // Skip on editor pages — the editor needs the localStorage data to load the pod
    if (!window.location.pathname.includes('/apps/editor')) {
      this.#clearLocalStorageForCartPods();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index (not used, kept for compatibility).
   * @param {Event} event - The click event.
   */
  onLineItemRemove(line, event) {
    // Find the cart item element from the clicked button or line index
    const cartItemRowToRemove =
      event?.target?.closest?.('.cart-item') || this.refs.cartItemRows?.[line - 1];

    if (!cartItemRowToRemove) return;

    const podUuid = cartItemRowToRemove.dataset.podUuid;
    const isPodMain = cartItemRowToRemove.classList.contains('cart-item--pod-main');
    const isPodSub = cartItemRowToRemove.classList.contains('cart-item--pod-sub');

    // NEW: Intercept POD sub-product removal - mark as inactive instead
    if (isPodSub) {
      // Add loading state
      cartItemRowToRemove.classList.add('cart-item--removing');

      // Find the actual line number in the cart based on the item key
      const itemKey = cartItemRowToRemove.dataset.key;
      return this.#markSubProductInactiveByKey(itemKey, cartItemRowToRemove);
    }

    // Collect all rows to remove
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove (bundle products)
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If this is a main POD product, also remove all sub-products with the same pod-uuid
    if (isPodMain && podUuid) {
      const podSubProducts = this.refs.cartItemRows.filter(
        (row) => row.dataset.podUuid === podUuid && row.classList.contains('cart-item--pod-sub')
      );
      rowsToRemove.push(...podSubProducts);

      // Clean up inactive items for this POD group
      this.#cleanupInactiveItemsForPod(podUuid);

      // Add loading state to the POD group
      const podGroup = cartItemRowToRemove.closest('.cart-items__pod-group');
      if (podGroup) {
        podGroup.classList.add('cart-items__pod-group--removing');
      }
    } else {
      // Add loading state to single item
      cartItemRowToRemove.classList.add('cart-item--removing');
    }

    // Remove duplicates
    const uniqueRowsToRemove = [...new Set(rowsToRemove)];

    // Get line numbers by finding items in cart by their keys
    this.#removeItemsByKeys(uniqueRowsToRemove, isPodMain && podUuid ? podUuid : null);
  }

  /**
   * Handles editing a POD item from the cart.
   * @param {{ pod_uuid?: string }} data - POD identifiers.
   * @param {Event} event - The click event.
   */
  async onEditPod(data, event) {
    event.preventDefault();

    const podUuid = data?.pod_uuid;

    if (!podUuid) {
      console.error('Missing pod UUID for edit');
      return;
    }

    const button = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = 'Loading...';
    }

    try {
      const podEditData = await this.#fetchPodJsonByUuid(podUuid);
      if (!podEditData) {
        throw new Error('POD JSON missing');
      }

      const podJson = this.#normalizePodJson(podEditData.podJson);
      localStorage.setItem(podEditData.storageKey, JSON.stringify(podJson));
      const localeRoot = window.Shopify?.routes?.root || '/';
      window.location.assign(`${localeRoot}apps/editor${podEditData.routeRoot}`);
    } catch (error) {
      console.error('Failed to load POD for edit:', error);
      if (button) {
        button.disabled = false;
        button.textContent = originalText || 'Edit';
      }
    }
  }

  /**
   * Handles copying (cloning) a POD item in the cart.
   * @param {{ pod_uuid?: string }} data - POD identifiers.
   * @param {Event} event - The click event.
   */
  async onCopyPod(data, event) {
    event.preventDefault();

    const podUuid = data?.pod_uuid;
    if (!podUuid) {
      console.error('Missing pod UUID for copy');
      return;
    }

    // Synchronous double-submit guard: bail out if a copy is already running.
    // This runs before any await, so a fast double-click (or a click on a button
    // we failed to disable) can never start a second clone+add sequence.
    if (this.#copyInProgress) {
      return;
    }
    this.#copyInProgress = true;

    const button = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = 'Loading...';
    }

    // Show loading state on the POD group
    const podGroup = button?.closest('.cart-items__pod-group');
    if (podGroup) {
      podGroup.classList.add('cart-items__pod-group--removing');
    }

    try {
      // Step 1: Call the clone endpoint via the app proxy
      const cloneResponse = await fetch(
        '/apps/editor/api/cart/add',
        fetchConfig('json', {
          body: JSON.stringify({ cloneFromUuid: podUuid }),
        })
      );

      if (!cloneResponse.ok) {
        const errorText = await cloneResponse.text();
        throw new Error(errorText || 'Clone request failed');
      }

      const result = await cloneResponse.json();
      if (!result.success) {
        throw new Error(result.error || 'Clone failed');
      }

      // Step 2: Build items array (accessories first, then main product)
      const items = [];

      // Add accessories
      if (result.accessories) {
        for (const accessory of result.accessories) {
          const variantId = parseInt(accessory.variantId.split('/').pop(), 10);
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              _article_id: accessory.articleId.toString(),
              _pod_cart_id: result.podCartId,
              _accessory_type: accessory.type,
              _free_with_pod: accessory.freeWithPod ? 'true' : 'false',
              _parent_pod_uuid: result.properties._pod_uuid,
            },
          });
        }
      }

      // Add main product
      const mainVariantId = parseInt(result.variantId.split('/').pop(), 10);
      items.push({
        id: mainVariantId,
        quantity: result.quantity,
        properties: result.properties,
      });

      // Step 3: Add all items to Shopify cart
      const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify({ items }),
      }));

      if (!addResponse.ok) {
        throw new Error('Failed to add cloned items to cart');
      }

      // Step 4: Refresh cart UI
      const cart = await this.#fetchCart();
      this.#dispatchCartUpdate(cart, null, this.#getFilteredItemCount(cart));
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      console.error('Failed to copy POD:', error);

      // Reset button state
      if (button) {
        button.disabled = false;
        button.textContent = originalText || 'Copy';
      }

      // Remove loading state
      if (podGroup) {
        podGroup.classList.remove('cart-items__pod-group--removing');
      }
    } finally {
      // Always release the guard so a later, legitimate copy can run. On success
      // the section is re-rendered with a fresh (enabled) Copy button.
      this.#copyInProgress = false;
    }
  }

  /**
   * Removes cart items by finding their line numbers from cart keys.
   * @param {HTMLElement[]} rowsToRemove - Array of row elements to remove.
   * @param {string|null} podUuid - POD UUID if removing a POD group, null otherwise.
   */
  async #removeItemsByKeys(rowsToRemove, podUuid = null) {
    try {
      this.#disableCartItems();

      const keys = rowsToRemove.map(row => row.dataset.key).filter(Boolean);

      // Collect keys to remove. Keys from DOM data-key attributes are always
      // original cart line keys (even for merged bundle components), so they
      // are valid for /cart/change.js regardless of cart transform state.
      const keysToRemove = new Set(keys);

      // Remove each item by key
      for (const key of keysToRemove) {
        await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity: 0 }),
        }));
      }

      const updatedCart = await this.#fetchCart();
      this.#dispatchCartUpdate(updatedCart, null, this.#getFilteredItemCount(updatedCart));
      await sectionRenderer.renderSection(this.sectionId, { cache: false });

      if (podUuid) {
        this.#deletePodFromPaApi(podUuid);
      }
    } catch (error) {
      console.error('Failed to remove items by keys:', error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Removes cart items in a single batched request.
   * @param {number[]} lineNumbers - Array of line numbers to remove.
   * @param {string|null} podUuid - POD UUID if removing a POD group, null otherwise.
   */
  async #removeItemsSequentially(lineNumbers, podUuid = null) {
    if (lineNumbers.length === 0) return;

    try {
      // OPTIMIZATION: Batch all removals into single request using variant IDs
      this.#disableCartItems();

      // Fetch current cart to get variant IDs
      const cart = await this.#fetchCart();

      // Build updates object with variant IDs: { "variant_id": 0, ... }
      const updates = {};
      for (const line of lineNumbers) {
        const item = cart.items[line - 1]; // Convert to 0-based index
        if (item) {
          // Use variant ID as key, set quantity to 0
          updates[item.variant_id] = 0;
        }
      }

      const body = {
        updates,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      };

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }));
      const cartData = await response.json();

      if (cartData.errors) {
        console.error('Failed to remove items:', cartData.errors);
        throw new Error(cartData.errors);
      }

      // Get updated cart for item count
      const updatedCart = await this.#fetchCart();

      // Dispatch event and morph section
      this.#dispatchCartUpdate(updatedCart, cartData.sections, this.#getFilteredItemCount(updatedCart));

      if (cartData.sections?.[this.sectionId]) {
        morphSection(this.sectionId, cartData.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      }

      if (podUuid) {
        this.#deletePodFromPaApi(podUuid);
      }
    } catch (error) {
      console.error('Failed to remove items:', error);
    }
  }

  /**
   * Removes a single item from the cart.
   * @param {number} line - The line number to remove.
   * @param {boolean} skipMorph - Whether to skip DOM morphing.
   * @returns {Promise<Object>} The cart response data.
   */
  async #removeItemFromCart(line, skipMorph = false) {
    // Wait for the API call to fully complete
    // This ensures line numbers don't shift during concurrent removals
    return await this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      skipMorph,
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} [config.skipMorph] - Skip DOM morphing (for batch operations).
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, skipMorph = false } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          throw new Error(parsedResponseText.errors);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // Skip morphing if requested (for batch operations)
        if (!skipMorph) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#updateCartQuantitySelectorButtonStates();
        }

        // Return the parsed response for batch operations that need it
        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();

      // Re-render inactive items after morphing
      this.#renderInactiveItems();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Parses inactive items from cart attributes.
   * @param {Object} cart - The cart object.
   * @returns {Object} Inactive items grouped by POD UUID.
   */
  #getInactiveItems(cart) {
    const attrValue = cart.attributes?._inactive_pod_subs;
    try {
      return attrValue ? JSON.parse(attrValue) : {};
    } catch {
      return {};
    }
  }

  /**
   * Calculates the cart item count excluding POD sub-products.
   * @param {Object} cart - The cart object.
   * @returns {number} Filtered item count.
   */
  #getFilteredItemCount(cart) {
    if (!cart.items) return 0;
    return cart.items.filter(item => !item.properties?._parent_pod_uuid).length;
  }

  /**
   * Marks a POD sub-product as inactive by finding it using its key.
   * @param {string} itemKey - The cart item key.
   * @param {HTMLElement} cartItemRow - The cart item row element.
   */
  async #markSubProductInactiveByKey(itemKey, cartItemRow) {
    const podUuid = cartItemRow.dataset.podUuid;
    if (!podUuid) {
      console.error('Cannot mark inactive: POD UUID not found');
      return;
    }

    try {
      // 1. Build item data from DOM attributes (works for both merged and non-merged)
      const variantId = parseInt(cartItemRow.dataset.variantId, 10);
      const productTitle = cartItemRow.dataset.productTitle
        || cartItemRow.querySelector('.cart-item__title')?.textContent?.trim()
        || 'Product';
      const imageEl = cartItemRow.querySelector('.cart-item__media-image');
      const image = cartItemRow.dataset.image || imageEl?.src || '';
      const accessoryType = cartItemRow.dataset.accessoryType || '';
      const articleId = cartItemRow.dataset.articleId || '';

      // Get the position of this item within the POD sub-products container
      const podSubsContainer = cartItemRow.closest('.cart-items__pod-subs');
      const allSubItems = Array.from(podSubsContainer.querySelectorAll('.cart-item--pod-sub:not(.cart-item--inactive)'));
      const positionIndex = allSubItems.indexOf(cartItemRow);

      // 2. Prepare inactive item data
      const inactiveItemData = {
        variant_id: variantId,
        quantity: 1,
        properties: {
          _parent_pod_uuid: podUuid,
          _accessory_type: accessoryType,
          _article_id: articleId,
          _free_with_pod: 'true',
        },
        key: itemKey,
        product_title: productTitle,
        image: image,
        position: positionIndex,
      };

      // 3. Read and update inactive items structure
      const cart = await this.#fetchCart();
      const inactiveItems = this.#getInactiveItems(cart);
      if (!inactiveItems[podUuid]) {
        inactiveItems[podUuid] = [];
      }
      inactiveItems[podUuid].push(inactiveItemData);

      // 4. Remove from cart and update attributes (use key-based removal)
      await this.#removeItemAndUpdateInactiveState(itemKey, inactiveItems);

      // 5. Update POD data in PA API
      if (accessoryType) {
        this.#removeAccessoryFromPaApi(podUuid, accessoryType);
      }
    } catch (error) {
      console.error('Failed to mark item inactive:', error);
    }
  }

  /**
   * Removes item from cart and updates inactive state atomically.
   * @param {string} itemKey - The line item key.
   * @param {Object} inactiveItems - Updated inactive items structure.
   */
  async #removeItemAndUpdateInactiveState(itemKey, inactiveItems) {
    // Use /cart/change.js for removal, then /cart/update.js for attributes with sections
    // (Can't combine because /cart/change.js doesn't support attributes parameter)

    this.#disableCartItems();

    try {
      // Step 1: Remove item by key via /cart/change.js
      const changeResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', { body: JSON.stringify({ id: itemKey, quantity: 0 }) })
      );

      if (!changeResponse.ok) {
        console.error('Failed to remove item by key:', await changeResponse.text());
      }

      // Step 2: Update attributes and get sections in same call
      const attributeCart = await this.#updateInactiveAttributes(inactiveItems, true);

      // Get updated cart data
      const cart = await this.#fetchCart();

      // Dispatch event and morph section
      this.#dispatchCartUpdate(cart, attributeCart.sections, this.#getFilteredItemCount(cart));
      this.#morphSectionFromSections(attributeCart.sections);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Renders inactive items from cart attributes.
   */
  #renderInactiveItems() {
    // Find all POD groups with inactive items data
    const podGroups = this.querySelectorAll('.cart-items__pod-group[data-inactive-items]');

    for (const podGroup of podGroups) {
      const podUuid = podGroup.dataset.podUuid;
      const inactiveDataStr = podGroup.dataset.inactiveItems;

      if (!inactiveDataStr || !podUuid) continue;

      try {
        const allInactiveItems = JSON.parse(inactiveDataStr);
        const inactiveForPod = allInactiveItems[podUuid];

        if (!inactiveForPod || inactiveForPod.length === 0) continue;

        // Find the sub-products container
        const subsContainer = podGroup.querySelector('.cart-items__pod-subs');
        if (!subsContainer) continue;

        // Render each inactive item at its original position
        for (const inactiveItem of inactiveForPod) {
          // Check if already rendered
          const existingInactive = subsContainer.querySelector(
            `[data-inactive-variant-id="${inactiveItem.variant_id}"]`
          );

          if (existingInactive) continue; // Skip if already rendered

          // Create inactive item element
          const inactiveEl = this.#createInactiveItemElement(inactiveItem, podUuid);

          // Insert at original position
          const position = inactiveItem.position ?? -1;
          const allItems = subsContainer.children;

          if (position >= 0 && position < allItems.length) {
            // Insert before the item at this position
            subsContainer.insertBefore(inactiveEl, allItems[position]);
          } else {
            // Fallback: append at end
            subsContainer.appendChild(inactiveEl);
          }
        }
      } catch (error) {
        console.error('Failed to render inactive items:', error);
      }
    }
  }

  /**
   * Creates an inactive item DOM element.
   * @param {Object} itemData - The inactive item data.
   * @param {string} podUuid - The POD UUID.
   * @returns {HTMLElement} The inactive item element.
   */
  #createInactiveItemElement(itemData, podUuid) {
    const div = document.createElement('div');
    div.className = 'cart-item cart-item--pod-sub cart-item--inactive';
    div.setAttribute('data-inactive-variant-id', itemData.variant_id);
    div.setAttribute('data-pod-uuid', podUuid);

    // Build product title and variant options
    const productTitle = itemData.product_title || 'Product';
    const variantOptions = this.#formatVariantOptions(itemData.properties);

    div.innerHTML = `
      <div class="cart-item__media-container" style="--ratio: 1;">
        ${
          itemData.image
            ? `
          <img
            src="${itemData.image}"
            alt="${productTitle}"
            class="cart-item__media-image border-style"
            style="opacity: 1; filter: grayscale(0.5);"
          />
        `
            : ''
        }
      </div>

      <div class="cart-item__details cart-primary-typography">
        <p class="cart-item__title">${productTitle}</p>
        ${
          variantOptions
            ? `
          <div class="cart-item__variants-compact">
            ${variantOptions}
          </div>
        `
            : ''
        }
      </div>

      <div class="cart-item__inactive-overlay">
        <div class="cart-item__inactive-content">
          <span class="cart-item__inactive-label">${this.dataset.removedText || 'Removed'}</span>
          <button
            class="cart-item__reactivate"
            type="button"
            on:click="/onReactivateSubProduct?variant_id=${itemData.variant_id}&pod_uuid=${podUuid}"
          >
            ${this.dataset.reactivateText || 'Reactivate'}
          </button>
        </div>
      </div>
    `;

    return div;
  }

  /**
   * Formats variant options from properties.
   * @param {Object} properties - The item properties.
   * @returns {string} Formatted variant options HTML.
   */
  #formatVariantOptions(properties) {
    if (!properties) return '';

    // Filter out internal properties (starting with _)
    const visibleOptions = Object.entries(properties)
      .filter(([key]) => !key.startsWith('_'))
      .map(([, value]) => `<span>${value}</span>`)
      .join('');

    return visibleOptions;
  }

  /**
   * Reactivates an inactive POD sub-product.
   * @param {Object} data - Data containing variant_id and pod_uuid.
   * @param {Event} event - The click event.
   */
  async onReactivateSubProduct(data, event) {
    event.preventDefault();
    const { variant_id, pod_uuid } = data;

    if (!variant_id || !pod_uuid) {
      console.error('Missing variant_id or pod_uuid for reactivation');
      return;
    }

    // Show loading state on button
    const button = event.target;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      // 1. Fetch current cart to get inactive items
      const cart = await this.#fetchCart();

      // 2. Get inactive item data
      const inactiveItems = this.#getInactiveItems(cart);
      const inactiveForPod = inactiveItems[pod_uuid] || [];

      const itemToReactivate = inactiveForPod.find((item) => item.variant_id === parseInt(variant_id));

      if (!itemToReactivate) {
        throw new Error('Inactive item not found');
      }

      // 3. Add item back to cart with sections request
      this.#disableCartItems();

      const properties = itemToReactivate.properties || {};

      const addBody = {
        items: [
          {
            id: itemToReactivate.variant_id,
            quantity: itemToReactivate.quantity,
            properties: properties,
          },
        ],
        sections: this.sectionId,
        sections_url: window.location.pathname,
      };

      const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(addBody) }));

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.description || 'Failed to add item');
      }

      const addResult = await addResponse.json();

      // Update POD data in PA API
      const accessoryType = properties._accessory_type;
      if (accessoryType) {
        this.#addAccessoryToPaApi(pod_uuid, accessoryType, properties._article_id);
      }

      // 4. Remove from inactive list and update attributes (with sections)
      const updatedInactiveForPod = inactiveForPod.filter((item) => item.variant_id !== parseInt(variant_id));

      if (updatedInactiveForPod.length === 0) {
        delete inactiveItems[pod_uuid];
      } else {
        inactiveItems[pod_uuid] = updatedInactiveForPod;
      }

      const attributeCart = await this.#updateInactiveAttributes(inactiveItems, true);

      // 5. Get final cart state for item count
      const finalCart = await this.#fetchCart();

      this.#dispatchCartUpdate(finalCart, attributeCart.sections, this.#getFilteredItemCount(finalCart));
      this.#morphSectionFromSections(attributeCart.sections);
    } catch (error) {
      console.error('Reactivation failed:', error);

      // Show error overlay on the inactive item
      this.#showReactivationError(button, error.message);

      // Reset button state
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  /**
   * Shows a reactivation error message.
   * @param {HTMLElement} button - The reactivate button.
   * @param {string} message - The error message.
   */
  #showReactivationError(button, message) {
    // Find the inactive overlay
    const overlay = button.closest('.cart-item__inactive-overlay');
    if (!overlay) return;

    // Check if error already exists
    const existingError = overlay.querySelector('.cart-item__inactive-error');
    if (existingError) {
      existingError.remove();
    }

    // Create error message element
    const errorEl = document.createElement('div');
    errorEl.className = 'cart-item__inactive-error';
    errorEl.textContent = message || 'Failed to reactivate item';

    overlay.appendChild(errorEl);

    // Remove after 3 seconds
    setTimeout(() => errorEl.remove(), 3000);
  }

  /**
   * Cleans up inactive items for a POD group.
   * @param {string} podUuid - The POD UUID to clean up.
   */
  async #cleanupInactiveItemsForPod(podUuid) {
    try {
      // Fetch current cart
      const cart = await this.#fetchCart();

      // Get and update inactive items
      const inactiveItems = this.#getInactiveItems(cart);

      // Remove all inactive items for this POD
      delete inactiveItems[podUuid];

      // Update cart attributes
      await this.#updateInactiveAttributes(inactiveItems);
    } catch (error) {
      console.error('Failed to cleanup inactive items:', error);
      // Non-critical error, continue with main product removal
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Fetches the latest cart data.
   * @returns {Promise<Object>} The cart data.
   */
  async #fetchCart() {
    const cartResponse = await fetch('/cart.js');
    return cartResponse.json();
  }

  /**
   * Updates inactive POD attributes.
   * @param {Object} inactiveItems - Updated inactive items structure.
   * @param {boolean} [includeSections=false] - Whether to request sections.
   * @returns {Promise<Object>} The attribute update response.
   */
  async #updateInactiveAttributes(inactiveItems, includeSections = false) {
    const hasInactiveItems = Object.keys(inactiveItems).length > 0;
    const body = {
      attributes: {
        _inactive_pod_subs: hasInactiveItems ? JSON.stringify(inactiveItems) : '',
      },
    };

    if (includeSections) {
      body.sections = this.sectionId;
      body.sections_url = window.location.pathname;
    }

    const attributeResponse = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    return attributeResponse.json();
  }

  /**
   * Fetches POD JSON from the app proxy by UUID.
   * @param {string} podUuid - The POD UUID.
   * @returns {Promise<{ podJson: Object, storageKey: string, routeRoot: string } | null>} POD edit data.
   */
  async #fetchPodJsonByUuid(podUuid) {
    const response = await fetch(
      '/apps/editor/api/pod/json',
      fetchConfig('json', { body: JSON.stringify({ podUuid }) })
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch POD JSON:', errorText);
      return null;
    }

    const data = await response.json();
    return data?.podEditData ?? null;
  }

  /**
   * Normalizes POD JSON to an object if the API returns a JSON string.
   * @param {Object|string} podJson - POD JSON or stringified JSON.
   * @returns {Object} Parsed POD JSON object.
   */
  #normalizePodJson(podJson) {
    if (typeof podJson === 'string') {
      try {
        return JSON.parse(podJson);
      } catch (error) {
        console.error('Failed to parse POD JSON:', error);
      }
    }
    return podJson;
  }

  /**
   * Notifies PA API that a POD accessory was removed from the cart.
   * @param {string} podUuid - The POD UUID.
   * @param {string} accessoryType - The accessory type.
   */
  async #removeAccessoryFromPaApi(podUuid, accessoryType) {
    if (!podUuid || !accessoryType) return;

    try {
      await fetch(
        '/apps/editor/api/pod/remove-accessory',
        fetchConfig('json', {
          body: JSON.stringify({ podUuid, accessoryType }),
        })
      );
    } catch (error) {
      console.error('Failed to update POD accessory data:', error);
    }
  }

  /**
   * Notifies PA API that a POD accessory was re-added to the cart.
   * @param {string} podUuid - The POD UUID.
   * @param {string} accessoryType - The accessory type.
   * @param {string|number} articleId - The article ID.
   */
  async #addAccessoryToPaApi(podUuid, accessoryType, articleId) {
    if (!podUuid || !accessoryType) return;

    try {
      await fetch(
        '/apps/editor/api/pod/add-accessory',
        fetchConfig('json', {
          body: JSON.stringify({ podUuid, accessoryType, articleId }),
        })
      );
    } catch (error) {
      console.error('Failed to update POD accessory data:', error);
    }
  }

  /**
   * Deletes a POD from PA API when a main POD product is removed.
   * @param {string} podUuid - The POD UUID to delete.
   */
  async #deletePodFromPaApi(podUuid) {
    if (!podUuid) return;

    this.#clearLocalStorageForPod(podUuid);

    try {
      const response = await fetch(
        '/apps/editor/api/pod/delete',
        fetchConfig('json', { body: JSON.stringify({ podUuid }) })
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to delete POD from PA API:', errorText);
      }
    } catch (error) {
      console.error('Failed to delete POD from PA API:', error);
    }
  }

  /**
   * Removes any localStorage entry whose stored podJson belongs to the deleted POD.
   * Pod editor stores podJson under type-based keys (e.g. 'pa-planner-a5-pod')
   * with the UUID at document.uuid in the stored JSON.
   * @param {string} podUuid - The POD UUID to search for.
   */
  #clearLocalStorageForPod(podUuid) {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('pa-')) continue;

        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data?.document?.uuid === podUuid) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // not valid JSON, skip
        }
      }
    } catch (e) {
      // localStorage may be unavailable in some contexts
    }
  }

  /**
   * Remove localStorage pod entries whose UUID matches a POD in the cart.
   * Called on page load (cart is already finalized) and on checkout submit.
   */
  clearLocalStorageForCartPods() {
    this.#clearLocalStorageForCartPods();
  }

  #clearLocalStorageForCartPods() {
    try {
      const cartPodUuids = new Set(
        (this.refs.cartItemRows || [])
          .map((row) => row.dataset.podUuid)
          .filter(Boolean)
      );

      if (cartPodUuids.size === 0) return;

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('pa-')) continue;

        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data?.document?.uuid && cartPodUuids.has(data.document.uuid)) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // not valid JSON, skip
        }
      }
    } catch (e) {
      // localStorage may be unavailable
    }
  }


  /**
   * Dispatches a cart update event.
   * @param {Object} cart - The cart data.
   * @param {Object} sections - Sections HTML data.
   * @param {number} itemCount - The cart item count.
   */
  #dispatchCartUpdate(cart, sections, itemCount) {
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections,
      })
    );
  }

  /**
   * Morphs the section if HTML is available, then updates related UI.
   * @param {Object} sections - Sections HTML data.
   */
  #morphSectionFromSections(sections) {
    if (!sections?.[this.sectionId]) return;

    morphSection(this.sectionId, sections[this.sectionId]);
    this.#updateCartQuantitySelectorButtonStates();
    this.#renderInactiveItems();
  }

  /**
   * Removes orphaned sub-products and their inactive entries on page load.
   * Orphans are items with _parent_pod_uuid whose main product (_pod_uuid)
   * no longer exists in the cart.
   */
  async #cleanupOrphanedSubProducts() {
    try {
      const cart = await this.#fetchCart();
      const items = cart.items || [];

      // Collect all pod UUIDs that have a main product in the cart
      const mainPodUuids = new Set();
      for (const item of items) {
        if (item.properties?._pod_uuid) {
          mainPodUuids.add(item.properties._pod_uuid);
        }
        // Also check within merged bundle components
        if (item.item_components) {
          for (const comp of item.item_components) {
            if (comp.properties?._pod_uuid) {
              mainPodUuids.add(comp.properties._pod_uuid);
            }
          }
        }
      }

      // Find orphaned active sub-products (top-level items with missing parent)
      const orphanedKeys = [];
      for (const item of items) {
        const parentUuid = item.properties?._parent_pod_uuid;
        if (parentUuid && !mainPodUuids.has(parentUuid)) {
          orphanedKeys.push(item.key);
        }
      }

      // Find orphaned inactive entries
      const inactiveItems = this.#getInactiveItems(cart);
      const orphanedInactiveUuids = Object.keys(inactiveItems).filter(
        (uuid) => !mainPodUuids.has(uuid)
      );

      if (orphanedKeys.length === 0 && orphanedInactiveUuids.length === 0) return;

      console.log(
        `[Cart Cleanup] Removing ${orphanedKeys.length} orphaned sub-products, ` +
        `${orphanedInactiveUuids.length} orphaned inactive entries`
      );

      // Remove active orphans by key
      for (const key of orphanedKeys) {
        await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity: 0 }),
        }));
      }

      // Clean up orphaned inactive entries
      if (orphanedInactiveUuids.length > 0) {
        for (const uuid of orphanedInactiveUuids) {
          delete inactiveItems[uuid];
        }
        await this.#updateInactiveAttributes(inactiveItems);
      }

      // Re-render the section and update cart count
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      const updatedCart = await this.#fetchCart();
      this.#dispatchCartUpdate(updatedCart, null, this.#getFilteredItemCount(updatedCart));
    } catch (error) {
      console.error('[Cart Cleanup] Failed to clean up orphaned sub-products:', error);
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
